// Work the app does lazily, said as it goes: a figure or bar still growing
// because some of what it counts is not counted yet (a file's diff not
// computed, a section's rows being checked again). The treatment is one
// for the whole app (components/Counting.svelte, the `counting` classes in
// layout.css): a quiet shimmer on bars, numbers muted and pulsing, how far
// along it is where that is known, and one announcement when it is done.
//
// Done is read from the stores' own state (a file's `exact`, a section's
// `checking`), never from a timer.

/** How much of what a figure counts is counted: `known` of `total` (files, pull requests). */
export interface Counted {
  known: number;
  total: number;
}

/** Whether a figure is still growing: some of what it counts is not counted yet. */
export const isCounting = (c: Counted): boolean => c.known < c.total;

/** Counts `known` of the items `items` for which `exact` holds. */
export function countedOf<T>(items: Iterable<T>, exact: (item: T) => boolean): Counted {
  let known = 0;
  let total = 0;
  for (const i of items) {
    total++;
    if (exact(i)) known++;
  }
  return { known, total };
}

const n = (x: number) => x.toLocaleString('en-US');
const noun = (x: number, one: string, many = `${one}s`) => (x === 1 ? one : many);

/** How far along it is, while counting ("counted 120 / 362 files"); null once everything is counted. */
export function countedNote(c: Counted, one: string, many?: string): string | null {
  return isCounting(c) ? `counted ${n(c.known)} / ${n(c.total)} ${noun(c.total, one, many)}` : null;
}

/** The same, spoken ("120 of 362 files counted so far."); null once everything is counted. */
export function countedSpoken(c: Counted, one: string, many?: string): string | null {
  return isCounting(c) ? `${n(c.known)} of ${n(c.total)} ${noun(c.total, one, many)} counted so far.` : null;
}

/**
 * What to announce as counting goes from `was` to `now`: once, when a
 * figure that was counting has everything counted ("All 362 files
 * counted."); nothing on the ticks between, nor for a figure that never
 * counted (it was whole when shown).
 */
export function settled(was: boolean, now: boolean, done: string): string | null {
  return was && !now ? done : null;
}
